import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { motion } from 'framer-motion';
import { WalletIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const Web3WalletConnect = ({ onWalletConnected, onApplicationSubmit }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletBalance, setWalletBalance] = useState('');
  const [networkName, setNetworkName] = useState('');
  const [error, setError] = useState('');
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationData, setApplicationData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    investmentAmount: '',
    investmentGoal: '',
    experience: '',
    sourceOfFunds: '',
    agreeToTerms: false
  });

  // Check if wallet is already connected on mount
  useEffect(() => {
    const checkExistingConnection = async () => {
      if (typeof window.ethereum !== 'undefined' && window.ethereum.selectedAddress) {
        try {
          const provider = new ethers.BrowserProvider(window.ethereum);
          const signer = await provider.getSigner();
          const address = await signer.getAddress();
          const balance = await provider.getBalance(address);
          const network = await provider.getNetwork();
          
          setWalletAddress(address);
          setWalletBalance(ethers.formatEther(balance));
          setNetworkName(network.name === 'unknown' ? `Chain ID: ${network.chainId}` : network.name);
          setIsConnected(true);
        } catch (err) {
          console.error('Error checking existing connection:', err);
        }
      }
    };

    checkExistingConnection();
  }, []);

  // Connect wallet function - using the same approach as your working project
  const connectWallet = async () => {
    if (typeof window.ethereum === 'undefined') {
      setError('No wallet detected. Please install MetaMask or another wallet extension.');
      return;
    }

    setIsConnecting(true);
    setError('');

    try {
      // Use the same approach as your working project
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      
      // Get balance and network info
      const balance = await provider.getBalance(address);
      const network = await provider.getNetwork();
      
      setWalletAddress(address);
      setWalletBalance(ethers.formatEther(balance));
      setNetworkName(network.name === 'unknown' ? `Chain ID: ${network.chainId}` : network.name);
      setIsConnected(true);
      
      // Notify parent component
      if (onWalletConnected) {
        onWalletConnected({
          address,
          balance: ethers.formatEther(balance),
          network: network.name === 'unknown' ? `Chain ID: ${network.chainId}` : network.name
        });
      }
      
    } catch (err) {
      console.error('Wallet connection error:', err);
      setError(err.message || 'Failed to connect wallet');
    } finally {
      setIsConnecting(false);
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    setWalletAddress('');
    setWalletBalance('');
    setNetworkName('');
    setIsConnected(false);
    setShowApplicationForm(false);
    setError('');
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setApplicationData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle application submission
  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch('https://api.segwaycapital.com/api/web3/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          walletAddress,
          walletBalance,
          networkName,
          ...applicationData
        }),
      });

      const result = await response.json();

      if (response.ok) {
        if (onApplicationSubmit) {
          onApplicationSubmit(result);
        }
        setError('');
        // Show success message or redirect
        alert('Application submitted successfully!');
      } else {
        setError(result.message || 'Failed to submit application');
      }
    } catch (err) {
      console.error('Error submitting application:', err);
      setError('Failed to submit application. Please try again.');
    }
  };

  // Event listeners for wallet changes
  useEffect(() => {
    if (!isConnected || !walletAddress) return;

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        disconnectWallet();
      } else if (accounts[0] !== walletAddress) {
        setWalletAddress(accounts[0]);
      }
    };

    const handleChainChanged = () => {
      window.location.reload();
    };

    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, [isConnected, walletAddress]);

  // Format wallet address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Wallet Connection Section */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Connect Your Web3 Wallet
        </h2>
        <p className="text-lg text-gray-600 mb-8">
          Connect your wallet to access our Web3 services and complete your application
        </p>

        {!isConnected ? (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={connectWallet}
            disabled={isConnecting}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-3 transition-colors"
          >
            <WalletIcon className="w-6 h-6" />
            {isConnecting ? 'Connecting...' : 'Connect Wallet'}
          </motion.button>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 border border-green-200 rounded-2xl p-6 max-w-md mx-auto"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
              <span className="text-xl font-semibold text-green-800">Wallet Connected!</span>
            </div>
            <div className="space-y-2 text-sm text-green-700">
              <p><strong>Address:</strong> {formatAddress(walletAddress)}</p>
              <p><strong>Balance:</strong> {parseFloat(walletBalance).toFixed(4)} ETH</p>
              <p><strong>Network:</strong> {networkName}</p>
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => setShowApplicationForm(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Complete Application
              </button>
              <button
                onClick={disconnectWallet}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Disconnect
              </button>
            </div>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 max-w-md mx-auto"
          >
            <div className="flex items-center gap-3">
              <XCircleIcon className="w-6 h-6 text-red-600" />
              <span className="text-red-800 font-medium">{error}</span>
            </div>
            <div className="mt-3">
              <a
                href="https://metamask.io/download/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Install MetaMask →
              </a>
            </div>
          </motion.div>
        )}
      </div>

      {/* Application Form */}
      {showApplicationForm && isConnected && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Complete Your Application</h3>
          <form onSubmit={handleSubmitApplication} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={applicationData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={applicationData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={applicationData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={applicationData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <input
                  type="text"
                  name="country"
                  value={applicationData.country}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Investment Amount (USD)</label>
                <input
                  type="number"
                  name="investmentAmount"
                  value={applicationData.investmentAmount}
                  onChange={handleInputChange}
                  required
                  min="1000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Investment Goal</label>
              <select
                name="investmentGoal"
                value={applicationData.investmentGoal}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select your investment goal</option>
                <option value="wealth-preservation">Wealth Preservation</option>
                <option value="income-generation">Income Generation</option>
                <option value="capital-appreciation">Capital Appreciation</option>
                <option value="diversification">Portfolio Diversification</option>
                <option value="retirement-planning">Retirement Planning</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Investment Experience</label>
              <select
                name="experience"
                value={applicationData.experience}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select your experience level</option>
                <option value="beginner">Beginner (0-2 years)</option>
                <option value="intermediate">Intermediate (3-5 years)</option>
                <option value="advanced">Advanced (5+ years)</option>
                <option value="professional">Professional</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Source of Funds</label>
              <select
                name="sourceOfFunds"
                value={applicationData.sourceOfFunds}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select source of funds</option>
                <option value="employment">Employment Income</option>
                <option value="business">Business Income</option>
                <option value="investment">Investment Returns</option>
                <option value="inheritance">Inheritance</option>
                <option value="savings">Personal Savings</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={applicationData.agreeToTerms}
                onChange={handleInputChange}
                required
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="text-sm text-gray-700">
                I agree to the terms and conditions and confirm that all information provided is accurate
              </label>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-xl font-semibold text-lg transition-colors"
            >
              Submit Application
            </button>
          </form>
        </motion.div>
      )}
    </div>
  );
};

export default Web3WalletConnect;
